import React from "react";
import { ChevronDown, Loader2, X } from "lucide-react";
import { UseControllerProps, useController } from "react-hook-form";

interface DropdownOption {
  value: string;
  label: string;
  isDisabled?: boolean;
}

interface DropdownProps extends UseControllerProps {
  options: DropdownOption[];
  placeholder?: string;
  label?: string;
  disable?: boolean;
  ref?: any;
  isLoading?: boolean;
  allowClear?: boolean;
}

const Dropdownselect: React.FC<DropdownProps> = ({
  options,
  placeholder = "Select an option",
  label,
  rules,
  disable = false,
  isLoading = false,
  allowClear = false,
  ...props
}) => {
  const {
    field: { value, onChange, onBlur },
    fieldState: { error },
  } = useController({
    ...props,
    rules,
  });

  const [isOpen, setIsOpen] = React.useState(false);
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleToggle = () => {
    if (!isLoading && !disable) setIsOpen(!isOpen);
  };

  const handleSelect = (option: DropdownOption) => {
    if (!option.isDisabled && !isLoading && !disable) {
      onChange(option.value); // Pass whole option object here
      setIsOpen(false);
    }
  };

  const handleClear = () => {
    if (!isLoading && !disable) {
      onChange(""); // Clear the whole object
      setIsOpen(false);
    }
  };

  const selectedOption = options.find((option) => option.value === value);

  return (
    <div className="relative w-full" ref={dropdownRef}>
      {label && (
        <label className="field-label">
          {label}
          {rules?.required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      <button
        type="button"
        className={`w-full p-3  border rounded-md flex items-center justify-between text-left focus:outline-none focus:ring-2 focus:ring-gray-200 ${
          error
            ? "border-red-500 bg-white"
            : disable
            ? "bg-gray-100 border-gray-300 text-gray-400"
            : "bg-white border-gray-300"
        } ${disable || isLoading ? "cursor-not-allowed" : "cursor-default"}`}
        onClick={handleToggle}
        onBlur={onBlur}
        disabled={disable || isLoading}
      >
        <span className="text-gray-600 flex-grow">
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <div className="flex items-center">
          {allowClear && selectedOption && !isLoading && !disable && (
            <button
              type="button"
              className="mr-2 text-gray-500 hover:text-gray-700 focus:outline-none"
              onClick={(e) => {
                e.stopPropagation(); // Prevent dropdown toggle
                handleClear();
              }}
            >
              <X className="w-4 h-4" />
            </button>
          )}
          {isLoading ? (
            <Loader2 className="w-5 h-5 animate-spin text-gray-500" />
          ) : (
            <ChevronDown
              className={`w-5 h-5 transition-transform ${
                isOpen ? "transform rotate-180" : ""
              }`}
            />
          )}
        </div>
      </button>
      {isOpen && !isLoading && !disable && (
        <ul className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
          {options.length > 0 ? (
            options.map((option) => (
              <li
                key={option.value}
                className={`px-2 py-2 cursor-pointer ${
                  option.isDisabled
                    ? "text-gray-500 bg-gray-100 cursor-not-allowed" // ✅ Disable headers
                    : "hover:bg-gray-100"
                }`}
                onClick={() => handleSelect(option)}
              >
                {option.label}
              </li>
            ))
          ) : (
            <li className="px-2 py-2 text-gray-500">No options available</li>
          )}
        </ul>
      )}
      {error && (
        <div className="text-red-500 text-sm mt-1">{error.message}</div>
      )}
    </div>
  );
};

export default Dropdownselect;
